/**
 * Sprint 5B — Betalingsopvolging tab (in Facturen sectie).
 * Toont de complete factuur-opvolgingstabel + filters + zoek + Nu-verwerken batch.
 * ALLE data komt van backend — géén frontend logica.
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, RefreshCcw, Send, Search, Filter, ChevronRight,
  History, Play, Zap, FileDown, Link as LinkIcon,
} from "lucide-react";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../ui/dialog";
import { Checkbox } from "../../ui/checkbox";
import { Label } from "../../ui/label";
import { FinancialStatusBadge, DueStatusBadge, FollowupStatusBadge } from "./StatusBadges";
import ReminderPreviewDialog from "./ReminderPreviewDialog";
import ReminderComposer from "./ReminderComposer";
import LinkInvoiceCustomerDialog from "./LinkInvoiceCustomerDialog";
import { nlTerm } from "../../../utils/pluralize";

const FILTERS = [
  { key: "all", label: "Alle" },
  { key: "vandaag", label: "Vandaag" },
  { key: "morgen", label: "Morgen" },
  { key: "komende_7_dagen", label: "Komende 7 dagen" },
  { key: "vervallen", label: "Vervallen" },
  { key: "deels_betaald", label: "Deels betaald" },
  { key: "gepauzeerd", label: "Gepauzeerd" },
  { key: "hold", label: "Hold" },
  { key: "betalingsregeling", label: "Betalingsregeling" },
  { key: "escalatie", label: "Escalatie" },
  { key: "mislukt", label: "Mislukt" },
  { key: "geen_email", label: "Geen geldig e-mailadres" },
  { key: "afgerond", label: "Afgerond" },
];

const STAGE_LABELS = {
  pre_reminder: "Pre-reminder",
  on_due: "Op vervaldatum",
  overdue_7: "+7d",
  overdue_14: "+14d",
  escalation_30: "+30d escalatie",
};

function fmt(x, currency = "EUR") {
  const s = Number(x || 0).toLocaleString("nl-NL", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
  return `${currency} ${s}`;
}

export default function BetalingsopvolgingTab() {
  const { user } = useAuth();
  const canRun = ["owner", "admin", "manager"].includes(user?.role);
  const canPreview = ["owner", "admin", "manager"].includes(user?.role);
  const canExportCsv = ["owner", "admin", "manager"].includes(user?.role);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [runDialogOpen, setRunDialogOpen] = useState(false);
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [composerInvoice, setComposerInvoice] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [linkInvoice, setLinkInvoice] = useState(null);
  // Bewaar de rij waarop de gebruiker Composer/Snel klikte terwijl er nog geen
  // klant gekoppeld is; na succesvolle koppeling openen we de originele actie.
  const [pendingActionAfterLink, setPendingActionAfterLink] = useState(null); // "composer" | "snel" | null

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/reminders/followup-overview", {
        params: { filter, search, limit: 200 },
      });
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const handleSearch = (e) => {
    if (e.key === "Enter") load();
  };

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const r = await adminApi.get(
        "/admin/customers-outstanding/export.csv",
        { responseType: "blob" },
      );
      const blob = new Blob([r.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // Filename komt uit Content-Disposition, fallback op datum
      const cd = r.headers?.["content-disposition"] || "";
      const m = /filename="?([^";]+)"?/i.exec(cd);
      a.download = m ? m[1] : `debiteurenrapport_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      const rowCount = r.headers?.["x-row-count"];
      toast.success(
        rowCount
          ? `Debiteurenrapport gedownload (${rowCount} factu${rowCount === "1" ? "ur" : "ren"}).`
          : "Debiteurenrapport gedownload.",
      );
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="betalingsopvolging-tab">
      {/* Toolbar */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-2 justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 text-xs text-gray-600">
            <Filter className="w-3.5 h-3.5" />
            <span>Filter:</span>
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger
              className="rounded-none h-8 min-w-[180px]"
              data-testid="betalingsopvolging-filter"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FILTERS.map((f) => (
                <SelectItem key={f.key} value={f.key}
                              data-testid={`betalingsopvolging-filter-${f.key}`}>
                  {f.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-2 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleSearch}
              placeholder="Zoek klant, factuurnr, e-mail..."
              className="rounded-none h-8 pl-7 text-xs w-64"
              data-testid="betalingsopvolging-search"
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline" size="sm" onClick={load}
            className="rounded-none h-8" data-testid="betalingsopvolging-refresh"
          >
            <RefreshCcw className="w-3.5 h-3.5 mr-1" /> Ververs
          </Button>
          {canExportCsv && (
            <Button
              variant="outline" size="sm"
              onClick={handleExportCsv}
              disabled={exporting}
              className="rounded-none h-8"
              data-testid="debiteurenrapport-export-csv-btn"
            >
              {exporting
                ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                : <FileDown className="w-3.5 h-3.5 mr-1" />}
              Exporteer CSV
            </Button>
          )}
          <Button
            variant="outline" size="sm" onClick={() => setHistoryOpen(true)}
            className="rounded-none h-8" data-testid="reminders-history-btn"
          >
            <History className="w-3.5 h-3.5 mr-1" /> Historie
          </Button>
          {canRun && (
            <Button
              onClick={() => setRunDialogOpen(true)}
              size="sm"
              className="rounded-none h-8 bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="reminders-run-now-btn"
            >
              <Zap className="w-3.5 h-3.5 mr-1" /> Herinneringen nu verwerken
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="border border-gray-200 bg-white overflow-x-auto">
        <table className="w-full text-xs" data-testid="betalingsopvolging-table">
          <thead className="bg-[#0A192F] text-white">
            <tr>
              <th className="p-2 text-left">Factuurnr</th>
              <th className="p-2 text-left">Klant</th>
              <th className="p-2 text-left">Vervaldatum</th>
              <th className="p-2 text-right">Openstaand</th>
              <th className="p-2 text-left">Financieel</th>
              <th className="p-2 text-left">Vervalstatus</th>
              <th className="p-2 text-left">Opvolging</th>
              <th className="p-2 text-left">Volgende</th>
              <th className="p-2 text-left">Laatste</th>
              <th className="p-2 text-center">Acties</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={10} className="p-6 text-center text-gray-500">
                  <Loader2 className="w-4 h-4 animate-spin inline mr-1" />
                  Laden...
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={10} className="p-6 text-center text-gray-500 italic"
                     data-testid="betalingsopvolging-empty">
                  Geen facturen gevonden voor dit filter.
                </td>
              </tr>
            ) : items.map((r) => (
              <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50"
                   data-testid={`betalingsopvolging-row-${r.id}`}>
                <td className="p-2 font-mono">{r.invoice_number}</td>
                <td className="p-2">
                  <div className="font-bold text-[#0A192F] flex items-center gap-1">
                    {r.customer_name || "—"}
                    {r.customer_linked === false && (
                      <span
                        className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 font-normal"
                        data-testid={`unlinked-customer-badge-${r.id}`}
                        title="Nog geen klant gekoppeld"
                      >
                        niet gekoppeld
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-gray-500">{r.customer_email || "geen e-mail"}</div>
                </td>
                <td className="p-2">{r.due_date || "—"}</td>
                <td className="p-2 text-right font-bold">
                  {fmt(r.amount_due, r.currency)}
                  {r.total_paid > 0 && (
                    <div className="text-[10px] text-gray-500">
                      /{fmt(r.total_incl_vat, r.currency)}
                    </div>
                  )}
                </td>
                <td className="p-2">
                  <FinancialStatusBadge status={r.financial_status} />
                </td>
                <td className="p-2">
                  <DueStatusBadge status={r.due_status} daysOverdue={r.days_overdue} />
                </td>
                <td className="p-2">
                  <FollowupStatusBadge status={r.followup_status || "actief"} />
                </td>
                <td className="p-2 text-[11px]">
                  {r.next_reminder_stage ? (
                    <div>
                      <div className="font-bold">{STAGE_LABELS[r.next_reminder_stage] || r.next_reminder_stage}</div>
                      <div className="text-[10px] text-gray-500">{r.next_reminder_date}</div>
                    </div>
                  ) : "—"}
                </td>
                <td className="p-2 text-[11px]">
                  {r.last_reminder_stage_sent ? (
                    <div>
                      <div className="font-bold">{STAGE_LABELS[r.last_reminder_stage_sent] || r.last_reminder_stage_sent}</div>
                      <div className="text-[10px] text-gray-500">
                        {r.last_reminder_sent_at
                          ? new Date(r.last_reminder_sent_at).toLocaleDateString("nl-NL")
                          : "—"}
                      </div>
                    </div>
                  ) : "—"}
                </td>
                <td className="p-2 text-center">
                  {canPreview && (
                    <div className="flex gap-1 justify-center">
                      {r.customer_linked === false ? (
                        <Button
                          size="sm"
                          onClick={() => setLinkInvoice(r)}
                          className="rounded-none h-7 text-[10px] bg-amber-500 hover:bg-amber-500/90 text-white"
                          data-testid={`reminder-link-customer-btn-${r.id}`}
                          title="Deze factuur is nog niet gekoppeld aan een klant"
                        >
                          <LinkIcon className="w-3 h-3 mr-1" /> Koppel klant
                        </Button>
                      ) : (
                        <>
                          <Button
                            size="sm" variant="outline"
                            onClick={() => setPreviewInvoice(r)}
                            className="rounded-none h-7 text-[10px]"
                            data-testid={`reminder-preview-btn-${r.id}`}
                            title="Snelle reminder (legacy preview)"
                          >
                            <Send className="w-3 h-3 mr-1" /> Snel
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => setComposerInvoice(r)}
                            className="rounded-none h-7 text-[10px] bg-[#0A192F] hover:bg-[#0A192F]/90 text-white"
                            data-testid={`reminder-composer-btn-${r.id}`}
                            title="Volledige Reminder Composer met bewerking"
                          >
                            <Send className="w-3 h-3 mr-1" /> Composer
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="text-[11px] text-gray-500">
        Totaal: {nlTerm("invoice", items.length)}. Alle
        beslissingen (stage-eligibility, due-status, idempotency) worden 100% door
        de backend genomen.
      </div>

      <BatchRunDialog
        open={runDialogOpen}
        onOpenChange={setRunDialogOpen}
        onComplete={() => load()}
      />
      <ReminderHistoryDialog
        open={historyOpen}
        onOpenChange={setHistoryOpen}
      />
      <ReminderPreviewDialog
        open={!!previewInvoice}
        onOpenChange={(v) => !v && setPreviewInvoice(null)}
        invoiceId={previewInvoice?.id}
        invoiceNumber={previewInvoice?.invoice_number}
        defaultStage={previewInvoice?.next_reminder_stage || "on_due"}
        onSent={() => load()}
      />
      <ReminderComposer
        open={!!composerInvoice}
        onOpenChange={(v) => !v && setComposerInvoice(null)}
        customerId={composerInvoice?.customer_id}
        focusInvoiceId={composerInvoice?.id}
        currentUserEmail={user?.email || ""}
        onSent={() => load()}
      />
      <LinkInvoiceCustomerDialog
        open={!!linkInvoice}
        onOpenChange={(v) => { if (!v) { setLinkInvoice(null); setPendingActionAfterLink(null); } }}
        invoice={linkInvoice}
        onLinked={(customerId) => {
          // Direct de tabel verversen
          load();
          // Open Composer met de nieuw gekoppelde klant (default flow)
          if (linkInvoice) {
            const nextInvoice = { ...linkInvoice, customer_id: customerId, customer_linked: true };
            if (pendingActionAfterLink === "snel") {
              setPreviewInvoice(nextInvoice);
            } else {
              setComposerInvoice(nextInvoice);
            }
          }
        }}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Batch run dialog
// ─────────────────────────────────────────────────────────────

function BatchRunDialog({ open, onOpenChange, onComplete }) {
  const [dryRun, setDryRun] = useState(false);
  const [stages, setStages] = useState([
    "pre_reminder", "on_due", "overdue_7", "overdue_14",
  ]);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      setDryRun(false);
      setResult(null);
    }
  }, [open]);

  const toggleStage = (k) => {
    setStages((cur) => cur.includes(k)
      ? cur.filter((x) => x !== k)
      : [...cur, k]);
  };

  const handleRun = async () => {
    setRunning(true);
    try {
      const r = await adminApi.post("/admin/reminders/run", {
        dry_run: dryRun, stage_keys: stages,
      });
      setResult(r.data);
      const c = r.data?.counts || {};
      const label = dryRun ? "Preview (dry-run)" : "Verwerking voltooid";
      toast.success(
        `${label}: ${c.planned || 0} gepland, ${c.sent || 0} verzonden, ${c.skipped || 0} overgeslagen, ${c.failed || 0} mislukt`
      );
      if (typeof onComplete === "function" && !dryRun) onComplete();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRunning(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg rounded-none"
        data-testid="reminders-run-confirm-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-chivo text-[#0A192F]">
            <Zap className="w-5 h-5" />
            Herinneringen nu verwerken
          </DialogTitle>
        </DialogHeader>

        <div className="border-l-4 border-amber-500 bg-amber-50 p-2 text-xs text-amber-900">
          Deze knop gebruikt exact dezelfde logica als de dagelijkse
          automatische scheduler. Idempotency-bescherming is actief — er
          worden geen dubbele mails verzonden.
        </div>

        <div className="space-y-2">
          <div className="text-xs font-bold text-[#0A192F]">Fases</div>
          {[
            ["pre_reminder", "Pre-reminder (3 dagen vóór)"],
            ["on_due", "Op vervaldatum"],
            ["overdue_7", "+7 dagen"],
            ["overdue_14", "+14 dagen"],
            ["escalation_30", "+30 dagen interne escalatie"],
          ].map(([k, label]) => (
            <label
              key={k}
              className="flex items-center gap-2 text-xs cursor-pointer"
            >
              <Checkbox
                checked={stages.includes(k)}
                onCheckedChange={() => toggleStage(k)}
                data-testid={`reminders-run-stage-${k}`}
              />
              <span>{label}</span>
            </label>
          ))}
          <label className="flex items-center gap-2 text-xs cursor-pointer mt-3 pt-3 border-t border-gray-200">
            <Checkbox
              checked={dryRun}
              onCheckedChange={(v) => setDryRun(!!v)}
              data-testid="reminders-run-dryrun"
            />
            <span>
              <strong>Dry-run</strong> (alleen preview — geen mails, geen
              schedule-mutaties, geen provider-calls)
            </span>
          </label>
        </div>

        {result && (
          <div className="border border-emerald-200 bg-emerald-50 p-2 text-xs text-emerald-900"
                data-testid="reminders-run-result-toast">
            <div className="font-bold">Resultaat</div>
            <div>Gepland: {result.counts?.planned || 0}</div>
            <div>Verzonden: {result.counts?.sent || 0}</div>
            <div>Overgeslagen: {result.counts?.skipped || 0}</div>
            <div>Mislukt: {result.counts?.failed || 0}</div>
            <div>Interne notif: {result.counts?.internal_notified || 0}</div>
            {result.dry_run && (
              <div className="italic mt-1">
                (Dry-run — er is niets verzonden)
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-none"
            disabled={running}
          >
            Sluiten
          </Button>
          <Button
            onClick={handleRun}
            disabled={running || stages.length === 0}
            className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="reminders-run-confirm"
          >
            {running ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Play className="w-4 h-4 mr-1" />}
            {dryRun ? "Preview draaien" : "Verwerken starten"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────
// History dialog
// ─────────────────────────────────────────────────────────────

function ReminderHistoryDialog({ open, onOpenChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const r = await adminApi.get("/admin/reminders/history?limit=50");
        setItems(r.data?.items || []);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-3xl rounded-none max-h-[85vh] overflow-y-auto"
        data-testid="reminders-history-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-chivo text-[#0A192F]">
            <History className="w-5 h-5" />
            Batch-run historie (laatste 50)
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="p-6 flex items-center gap-2 text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Laden...
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-gray-100">
              <tr>
                <th className="p-2 text-left">Datum</th>
                <th className="p-2 text-left">Trigger</th>
                <th className="p-2 text-left">Door</th>
                <th className="p-2 text-right">Gepland</th>
                <th className="p-2 text-right">Verzonden</th>
                <th className="p-2 text-right">Overgeslagen</th>
                <th className="p-2 text-right">Mislukt</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-t border-gray-100">
                  <td className="p-2 font-mono text-[10px]">{r.started_at}</td>
                  <td className="p-2">{r.trigger}</td>
                  <td className="p-2">{r.triggered_by}</td>
                  <td className="p-2 text-right">{r.counts?.planned || 0}</td>
                  <td className="p-2 text-right">{r.counts?.sent || 0}</td>
                  <td className="p-2 text-right">{r.counts?.skipped || 0}</td>
                  <td className="p-2 text-right">{r.counts?.failed || 0}</td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-6 text-center text-gray-500 italic">
                    Geen batch-runs uitgevoerd.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </DialogContent>
    </Dialog>
  );
}
